/**
 * Admin-side Business Details panel — mounted inside the PWS partner
 * drawer (Feb 2026).
 *
 * Purpose: an admin can fill in ABN / GST / physical + postal address
 * on the partner's behalf so the "Finish your Business Details" banner
 * stops nagging them on every partner-portal page. Uses the same
 * `PUT /api/admin/partners/{id}/business-details` endpoint that the
 * partner-side form saves to — status calc + audit trail are shared.
 *
 * Also exposes a **"Mark complete (override)"** button that hits
 * `POST /admin/partners/{id}/business-details/mark-complete`. This
 * flips `business_details_status = "complete"` without collecting the
 * underlying ABN / addresses, for partners whose data is held
 * off-platform (e.g. Cooloola pre-invite).
 *
 * Additive only — the partner-side onboarding flow is untouched.
 */
import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, Save, Loader2, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

const EMPTY_ADDR = { line1: "", line2: "", suburb: "", state: "", postcode: "", country: "AU", formatted: "" };
const EMPTY_FORM = {
  legal_name: "",
  trading_name: "",
  abn: "",
  gst_registered: false,
  physical_address: { ...EMPTY_ADDR },
  postal_address: { ...EMPTY_ADDR },
  postal_same_as_physical: true,
};

const AU_STATES = ["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"];

function StatusPill({ status }) {
  const tone = {
    missing:    "bg-slate-100 text-slate-700 border-slate-200",
    incomplete: "bg-amber-50 text-amber-800 border-amber-200",
    complete:   "bg-emerald-50 text-emerald-800 border-emerald-200",
  }[status] || "bg-slate-100 text-slate-700 border-slate-200";
  const label = { missing: "Not started", incomplete: "In progress", complete: "Complete" }[status] || status;
  return (
    <span
      data-testid="admin-business-details-status-pill"
      className={`text-xs px-2.5 py-1 rounded-full border font-medium ${tone}`}
    >
      {label}
    </span>
  );
}

function AddrRow({ prefix, value, onChange }) {
  const v = value || { ...EMPTY_ADDR };
  const patch = (partial) => onChange({ ...v, ...partial });
  return (
    <div className="grid grid-cols-2 gap-2">
      <div className="col-span-2">
        <label className="text-[11px] font-medium text-slate-600">Line 1</label>
        <Input
          data-testid={`${prefix}-line1`}
          value={v.line1 || ""}
          onChange={(e) => patch({ line1: e.target.value })}
          placeholder="12 Coastal Rd"
          className="mt-0.5 text-sm"
        />
      </div>
      <div>
        <label className="text-[11px] font-medium text-slate-600">Suburb</label>
        <Input
          data-testid={`${prefix}-suburb`}
          value={v.suburb || ""}
          onChange={(e) => patch({ suburb: e.target.value })}
          placeholder="Noosaville"
          className="mt-0.5 text-sm"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[11px] font-medium text-slate-600">State</label>
          <select
            data-testid={`${prefix}-state`}
            value={v.state || ""}
            onChange={(e) => patch({ state: e.target.value })}
            className="mt-0.5 h-9 w-full rounded-md border border-slate-200 bg-white text-sm px-2"
          >
            <option value="">—</option>
            {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-medium text-slate-600">Postcode</label>
          <Input
            data-testid={`${prefix}-postcode`}
            value={v.postcode || ""}
            onChange={(e) => patch({ postcode: e.target.value.replace(/\D/g, "").slice(0, 4) })}
            placeholder="4566"
            className="mt-0.5 text-sm"
            inputMode="numeric"
          />
        </div>
      </div>
    </div>
  );
}

export default function PartnerBusinessDetailsPanel({ partnerId, prefill }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState("missing");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [overriding, setOverriding] = useState(false);
  const [fieldErr, setFieldErr] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/partners/${partnerId}/business-details`);
      setStatus(data?.status || "missing");
      setForm({
        legal_name:              data?.legal_name || prefill?.business_name || "",
        trading_name:            data?.trading_name || "",
        abn:                     data?.abn || prefill?.abn || "",
        gst_registered:          !!data?.gst_registered,
        physical_address:        data?.physical_address?.line1
          ? data.physical_address
          : {
              ...EMPTY_ADDR,
              line1: prefill?.office_address || "",
              state: prefill?.state || "QLD",
            },
        postal_address:          data?.postal_address || { ...EMPTY_ADDR },
        postal_same_as_physical: data?.postal_same_as_physical ?? true,
      });
    } catch {
      toast.error("Couldn't load business details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [partnerId]);

  const patchForm = (partial) => setForm((f) => ({ ...f, ...partial }));

  const onSave = async () => {
    setSaving(true); setFieldErr({});
    try {
      const payload = { ...form };
      if (form.postal_same_as_physical) payload.postal_address = form.physical_address;
      const { data } = await api.put(`/admin/partners/${partnerId}/business-details`, payload);
      setStatus(data?.status || "missing");
      toast.success(
        data?.status === "complete"
          ? "Business details saved — status is now Complete."
          : "Draft saved — some required fields still need filling.",
      );
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.field) {
        setFieldErr({ [detail.field]: detail.message });
        toast.error(`${detail.field}: ${detail.message}`);
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to save");
      }
    } finally {
      setSaving(false);
    }
  };

  const onMarkComplete = async () => {
    if (!window.confirm(
      "Mark this partner's Business Details as complete without filling in ABN / addresses?\n\n" +
      "Use this only when you're holding their legal data off-platform. " +
      "The partner will stop seeing the onboarding banner immediately.",
    )) return;
    const reason = window.prompt("Reason for the override? (recorded in the audit log)") || "";
    setOverriding(true);
    try {
      const { data } = await api.post(`/admin/partners/${partnerId}/business-details/mark-complete`, { reason });
      setStatus(data?.status || "complete");
      toast.success("Marked complete — partner banner will disappear on next page load.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to mark complete");
    } finally {
      setOverriding(false);
    }
  };

  if (loading) {
    return (
      <div
        data-testid="admin-business-details-panel"
        className="mt-5 rounded-2xl border border-slate-200 bg-slate-50/50 p-4 text-xs text-slate-500 flex items-center gap-2"
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading business details…
      </div>
    );
  }

  return (
    <div
      data-testid="admin-business-details-panel"
      className="mt-5 rounded-2xl border border-slate-200 bg-slate-50/50 p-4"
    >
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> Business details (payout data)
          </div>
          <div className="text-xs text-slate-500 max-w-lg">
            Fill these in on the partner&apos;s behalf so they don&apos;t see
            the &ldquo;Finish your Business Details&rdquo; banner. Same data
            the partner-side form captures — legal name, ABN, GST, physical
            + postal address.
          </div>
        </div>
        <StatusPill status={status} />
      </div>

      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[11px] font-medium text-slate-600">Legal business name</label>
            <Input
              data-testid="admin-bd-legal-name"
              value={form.legal_name}
              onChange={(e) => patchForm({ legal_name: e.target.value })}
              placeholder="Cooloola Water Pty Ltd"
              className="mt-0.5 text-sm"
            />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-600">Trading name (optional)</label>
            <Input
              data-testid="admin-bd-trading-name"
              value={form.trading_name}
              onChange={(e) => patchForm({ trading_name: e.target.value })}
              placeholder="PWS Cooloola"
              className="mt-0.5 text-sm"
            />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-600">ABN</label>
            <Input
              data-testid="admin-bd-abn"
              value={form.abn}
              onChange={(e) => patchForm({ abn: e.target.value.replace(/[^0-9\s-]/g, "").slice(0, 20) })}
              placeholder="51 824 753 556"
              className="mt-0.5 font-mono text-sm"
              inputMode="numeric"
            />
            {fieldErr.abn && <div className="text-[11px] text-rose-700 mt-0.5">{fieldErr.abn}</div>}
          </div>
          <div className="flex items-end pb-1">
            <label className="inline-flex items-center gap-2 text-sm text-slate-800 cursor-pointer">
              <input
                type="checkbox"
                data-testid="admin-bd-gst"
                checked={form.gst_registered}
                onChange={(e) => patchForm({ gst_registered: e.target.checked })}
                className="h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
              />
              GST registered
            </label>
          </div>
        </div>

        <div>
          <div className="text-[11px] font-medium text-slate-600 mb-1">Physical address</div>
          <AddrRow
            prefix="admin-bd-physical"
            value={form.physical_address}
            onChange={(next) => patchForm({ physical_address: next })}
          />
          {fieldErr.physical_address && (
            <div className="text-[11px] text-rose-700 mt-1">{fieldErr.physical_address}</div>
          )}
        </div>

        <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
          <input
            type="checkbox"
            data-testid="admin-bd-postal-same"
            checked={form.postal_same_as_physical}
            onChange={(e) => patchForm({ postal_same_as_physical: e.target.checked })}
            className="h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
          />
          Postal address is the same as physical
        </label>

        {!form.postal_same_as_physical && (
          <div>
            <div className="text-[11px] font-medium text-slate-600 mb-1">Postal address</div>
            <AddrRow
              prefix="admin-bd-postal"
              value={form.postal_address}
              onChange={(next) => patchForm({ postal_address: next })}
            />
            {fieldErr.postal_address && (
              <div className="text-[11px] text-rose-700 mt-1">{fieldErr.postal_address}</div>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap pt-1">
          <Button
            data-testid="admin-bd-save-btn"
            onClick={onSave}
            disabled={saving}
            className="bg-slate-900 hover:bg-cyan-700 text-white h-9"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-2" />}
            {saving ? "Saving…" : "Save details"}
          </Button>
          {status !== "complete" && (
            <Button
              data-testid="admin-bd-mark-complete-btn"
              onClick={onMarkComplete}
              disabled={overriding}
              variant="outline"
              className="h-9"
              title="Force status to Complete without collecting ABN / addresses — use when the data is held off-platform."
            >
              {overriding ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-2" />}
              Mark complete (override)
            </Button>
          )}
          {status === "complete" && (
            <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> Partner banner is hidden
            </span>
          )}
        </div>

        <div className="text-[11px] text-slate-500 flex items-start gap-1.5 pt-1">
          <AlertTriangle className="w-3 h-3 mt-0.5 flex-none text-amber-600" />
          The partner can still update these details themselves at any time
          from their portal. Every save writes an audit row.
        </div>
      </div>
    </div>
  );
}
